import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getIssue, updateIssue, createOrder, getSuppliers, getOrders, getEmailThread, deleteIssue, sendIssueResolvedEmail, sendSupplierAssignmentEmail, sendWorkOrderSMS } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft,
  Wrench,
  CheckCircle,
  File,
  EnvelopeSimple,
  House,
  Truck,
  Calendar,
  Tag,
  Warning,
  Clock,
  User,
  Phone,
  ChatText,
  CaretDown,
  Trash,
  DeviceMobile
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import IssueConversation from '../components/IssueConversation';
import {
  IssueData,
  SupplierData,
  OrderData,
  OrderFormData,
  EmailThread,
  URGENCY_LABELS
} from '../components/issues';
import { OrderViewDialog } from '../components/issues/OrderViewDialog';
import { CreateOrderDialog } from '../components/issues/CreateOrderDialog';

export default function IssueDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  const [issue, setIssue] = useState<IssueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [suppliers, setSuppliers] = useState<SupplierData[]>([]);
  const [emailThread, setEmailThread] = useState<EmailThread | null>(null);
  const [existingOrder, setExistingOrder] = useState<OrderData | null>(null);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderViewOpen, setOrderViewOpen] = useState(false);
  const [sendingResolvedEmail, setSendingResolvedEmail] = useState(false);
  const [sendingSupplierEmail, setSendingSupplierEmail] = useState(false);
  const [sendingSupplierSMS, setSendingSupplierSMS] = useState(false);
  const [orderForm, setOrderForm] = useState<OrderFormData>({
    subcontractor_name: '',
    subcontractor_email: '',
    subcontractor_phone: '',
    instructions: '',
    instructions_html: '',
    due_date: '',
    include_history: true,
    include_internal_notes: false
  });
  const [selectedSupplierForOrder, setSelectedSupplierForOrder] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // Fetch issue and related data
  useEffect(() => {
    if (!id) return;
    
    const fetchData = async () => {
      try {
        const [issueRes, suppliersRes] = await Promise.all([
          getIssue(id),
          getSuppliers()
        ]);
        setIssue(issueRes.data);
        setSuppliers(suppliersRes.data);
        
        // Fetch existing order for this issue
        try {
          const ordersRes = await getOrders({ issue_id: id });
          if (ordersRes.data && ordersRes.data.length > 0) {
            setExistingOrder(ordersRes.data[0]);
          }
        } catch (e) {
          // No order exists yet
        }
        
        // Fetch email thread if exists
        const threadId = issueRes.data?.email_thread_id;
        if (threadId) {
          try {
            const threadRes = await getEmailThread(threadId);
            setEmailThread(threadRes.data);
          } catch (e) {
            console.log('No email thread found');
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        navigate('/issues');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  // Build conversation history as HTML
  const buildConversationHTML = useCallback((includeNotes = false) => {
    if (!issue) return '';
    
    const urgencyLabels: Record<number, string> = {1: 'Låg', 2: 'Normal', 3: 'Medel', 4: 'Hög', 5: 'Akut'};
    const urgency = urgencyLabels[issue.urgency_level || 2] || 'Normal';
    
    let html = `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; background: #f8fafc; padding: 20px; border-radius: 12px;">
        <!-- Header -->
        <div style="background: linear-gradient(135deg, #1e293b 0%, #334155 100%); color: white; padding: 20px; border-radius: 10px; margin-bottom: 20px;">
          <div style="font-size: 12px; opacity: 0.8; margin-bottom: 4px;">${issue.issue_number || 'Ärende'}</div>
          <h2 style="margin: 0; font-size: 18px; font-weight: 600;">${issue.title || 'Utan titel'}</h2>
          <div style="margin-top: 12px; display: flex; gap: 8px; flex-wrap: wrap;">
            <span style="background: rgba(255,255,255,0.2); padding: 4px 10px; border-radius: 20px; font-size: 11px;">${t(issue.category || 'other')}</span>
            <span style="background: rgba(255,255,255,0.2); padding: 4px 10px; border-radius: 20px; font-size: 11px;">Brådska: ${urgency}</span>
          </div>
        </div>
    `;
    
    // Description
    if (issue.description) {
      html += `
        <div style="background: white; border-radius: 10px; padding: 16px; margin-bottom: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
          <div style="font-size: 11px; font-weight: 600; color: #64748b; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">Ärendebeskrivning</div>
          <p style="margin: 0; font-size: 14px; color: #334155; line-height: 1.6; white-space: pre-wrap;">${issue.description}</p>
        </div>
      `;
    }
    
    // Email conversation - skip first message if it matches description
    if (emailThread?.messages && emailThread.messages.length > 0) {
      // Filter out the first message if it's the same as description
      const messages = emailThread.messages;
      const messagesToShow = messages.filter((msg, idx) => {
        if (idx === 0 && issue.description) {
          // Check if first message body matches description (roughly)
          const descClean = issue.description.trim().substring(0, 100);
          const bodyClean = (msg.body || '').trim().substring(0, 100);
          if (descClean === bodyClean) {
            return false; // Skip this message
          }
        }
        return true;
      });
      
      if (messagesToShow.length > 0) {
        html += `
          <div style="margin-bottom: 16px;">
            <div style="font-size: 11px; font-weight: 600; color: #64748b; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Konversation</div>
        `;
        
        messagesToShow.forEach((msg) => {
          const date = msg.created_at ? new Date(msg.created_at).toLocaleString('sv-SE') : '';
          const isOutbound = msg.direction === 'outbound';
          const sender = msg.from_email?.split('@')[0] || 'Okänd';
          const body = msg.body || '';
          
          if (isOutbound) {
            // Right-aligned (outbound - from admin)
            html += `
              <div style="display: flex; justify-content: flex-end; margin-bottom: 12px;">
                <div style="background: #1e293b; color: white; padding: 12px 16px; border-radius: 16px 16px 4px 16px; max-width: 80%;">
                  <div style="font-size: 11px; opacity: 0.7; margin-bottom: 4px;">${sender} • ${date}</div>
                  <div style="font-size: 13px; line-height: 1.5; white-space: pre-wrap;">${body.substring(0, 500)}</div>
                </div>
              </div>
            `;
          } else {
            // Left-aligned (inbound - from reporter)
            html += `
              <div style="display: flex; justify-content: flex-start; margin-bottom: 12px;">
                <div style="background: white; border: 1px solid #e2e8f0; padding: 12px 16px; border-radius: 16px 16px 16px 4px; max-width: 80%; box-shadow: 0 1px 2px rgba(0,0,0,0.05);">
                  <div style="font-size: 11px; color: #64748b; margin-bottom: 4px;">${sender} • ${date}</div>
                  <div style="font-size: 13px; line-height: 1.5; color: #334155; white-space: pre-wrap;">${body.substring(0, 500)}</div>
                </div>
              </div>
            `;
          }
        });
        
        html += `</div>`;
      }
    }
    
    // Internal notes (GREEN) - only if includeNotes is true
    if (includeNotes && issue?.internal_notes && issue.internal_notes.length > 0) {
      html += `
        <div style="margin-bottom: 16px;">
          <div style="font-size: 11px; font-weight: 600; color: #059669; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Interna anteckningar</div>
      `;
      
      issue.internal_notes.forEach((note) => {
        const date = note.created_at ? new Date(note.created_at).toLocaleString('sv-SE') : '';
        html += `
          <div style="display: flex; justify-content: flex-start; margin-bottom: 12px;">
            <div style="background: #d1fae5; border: 1px solid #a7f3d0; padding: 12px 16px; border-radius: 10px; max-width: 85%;">
              <div style="font-size: 11px; color: #059669; margin-bottom: 4px;">${note.author_name || 'Intern'} • ${date}</div>
              <div style="font-size: 13px; line-height: 1.5; color: #065f46; white-space: pre-wrap;">${note.text}</div>
            </div>
          </div>
        `;
      });
      
      html += `</div>`;
    }
    
    // Reporter contact info
    html += `
      <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; padding: 16px;">
        <div style="font-size: 11px; font-weight: 600; color: #166534; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">Kontaktuppgifter ärendeägare</div>
        <div style="font-size: 13px; color: #166534;">
    `;
    
    if (issue.reporter_name) html += `<div style="margin-bottom: 4px;"><strong>Namn:</strong> ${issue.reporter_name}</div>`;
    if (issue.reporter_email) html += `<div style="margin-bottom: 4px;"><strong>E-post:</strong> ${issue.reporter_email}</div>`;
    if (issue.reporter_phone) html += `<div><strong>Telefon:</strong> ${issue.reporter_phone}</div>`;
    if (!issue.reporter_name && !issue.reporter_email && !issue.reporter_phone) {
      html += `<div style="color: #64748b; font-style: italic;">Inga kontaktuppgifter registrerade</div>`;
    }
    
    html += `
        </div>
      </div>
    </div>
    `;
    
    return html;
  }, [issue, emailThread, t]);

  // Build plain text version for form
  const buildConversationText = useCallback((includeNotes = false) => {
    if (!issue) return '';
    
    const lines: string[] = [];
    lines.push(`ÄRENDE: ${issue.issue_number || 'N/A'}`);
    lines.push(`TITEL: ${issue.title || 'Utan titel'}`);
    const categoryKey = issue.category ?? 'other';
    lines.push(`KATEGORI: ${t(categoryKey)}`);
    lines.push('');
    
    if (issue.description) {
      lines.push('ÄRENDEBESKRIVNING:');
      lines.push(issue.description);
      lines.push('');
    }
    
    if (emailThread?.messages && emailThread.messages.length > 0) {
      // Filter out the first message if it matches description
      const messagesToShow = emailThread.messages.filter((msg, idx) => {
        if (idx === 0 && issue.description) {
          const descClean = issue.description.trim().substring(0, 100);
          const bodyClean = (msg.body || '').trim().substring(0, 100);
          if (descClean === bodyClean) {
            return false;
          }
        }
        return true;
      });
      
      if (messagesToShow.length > 0) {
        lines.push('KONVERSATION:');
        messagesToShow.forEach((msg) => {
          const date = msg.created_at ? new Date(msg.created_at).toLocaleString('sv-SE') : '';
          const sender = msg.from_email || 'Okänd';
          const direction = msg.direction === 'outbound' ? '→' : '←';
          lines.push(`${direction} ${sender} (${date}):`);
          lines.push(msg.body || '');
          lines.push('');
        });
      }
    }
    
    // Internal notes (if requested)
    if (includeNotes && issue.internal_notes && issue.internal_notes.length > 0) {
      lines.push('INTERNA ANTECKNINGAR:');
      issue.internal_notes.forEach((note) => {
        const date = note.created_at ? new Date(note.created_at).toLocaleString('sv-SE') : '';
        lines.push(`[${note.author_name || 'Intern'}] (${date}):`);
        lines.push(note.text);
        lines.push('');
      });
    }
    
    lines.push('KONTAKT ÄRENDEÄGARE:');
    if (issue.reporter_name) lines.push(`Namn: ${issue.reporter_name}`);
    if (issue.reporter_email) lines.push(`E-post: ${issue.reporter_email}`);
    if (issue.reporter_phone) lines.push(`Telefon: ${issue.reporter_phone}`);
    
    return lines.join('\n');
  }, [issue, emailThread, t]);

  // Handle supplier selection for order
  const handleSupplierSelect = useCallback((supplierId: string) => {
    setSelectedSupplierForOrder(supplierId);
    const supplier = suppliers.find(s => s.id === supplierId);
    if (supplier) {
      setOrderForm(prev => ({
        ...prev,
        subcontractor_name: supplier.name || '',
        subcontractor_email: supplier.email || '',
        subcontractor_phone: supplier.phone || ''
      }));
    }
  }, [suppliers]);

  // Handle include history toggle
  const handleIncludeHistoryChange = useCallback((checked: boolean) => {
    setOrderForm(prev => {
      const newForm = { ...prev, include_history: checked };
      if (checked) {
        newForm.instructions = buildConversationText(prev.include_internal_notes);
        newForm.instructions_html = buildConversationHTML(prev.include_internal_notes);
      } else {
        newForm.instructions = '';
        newForm.instructions_html = '';
      }
      return newForm;
    });
  }, [buildConversationText, buildConversationHTML]);

  // Initialize order form when dialog opens
  const handleOrderDialogOpen = useCallback((open: boolean) => {
    setOrderDialogOpen(open);
    if (open && issue) {
      // Pre-select assigned supplier if any
      if (issue.supplier_id) {
        handleSupplierSelect(issue.supplier_id);
      }
      // Pre-fill with history (without internal notes by default)
      setOrderForm(prev => ({
        ...prev,
        include_history: true,
        include_internal_notes: false,
        instructions: buildConversationText(false),
        instructions_html: buildConversationHTML(false)
      }));
    } else {
      // Reset form
      setOrderForm({
        subcontractor_name: '',
        subcontractor_email: '',
        subcontractor_phone: '',
        instructions: '',
        instructions_html: '',
        due_date: '',
        include_history: true
      });
      setSelectedSupplierForOrder('');
    }
  }, [issue, handleSupplierSelect, buildConversationText, buildConversationHTML]);

  const handleStatusChange = async (status: string) => {
    if (!id) return;
    setUpdating(true);
    try {
      const { data } = await updateIssue(id, { status: status as 'open' | 'in_progress' | 'waiting' | 'resolved' | 'closed' }, {
        sendNotification: true,
        previousIssue: issue,
        organizationId: issue?.organization_id
      });
      setIssue(data);
      toast.success(t('issue_updated'));
    } catch (error) {
      toast.error(t('error_occurred'));
    } finally {
      setUpdating(false);
    }
  };

  // Send resolved email to reporter
  const handleSendResolvedEmail = async () => {
    if (!issue?.reporter_email) {
      toast.error('Ingen e-postadress för rapportören');
      return;
    }
    
    setSendingResolvedEmail(true);
    try {
      await sendIssueResolvedEmail(issue, issue.reporter_email || '', issue.reporter_name || '');
      toast.success('E-post skickad till rapportören');
    } catch (error) {
      console.error('Failed to send resolved email:', error);
      toast.error('Kunde inte skicka e-post');
    } finally {
      setSendingResolvedEmail(false);
    }
  };

  // Send assignment email to supplier
  const handleSendSupplierEmail = async () => {
    if (!issue?.supplier_id) {
      toast.error('Ingen leverantör tilldelad');
      return;
    }
    
    const supplier = suppliers.find(s => s.id === issue.supplier_id);
    if (!supplier?.email) {
      toast.error('Leverantören saknar e-postadress');
      return;
    }
    
    setSendingSupplierEmail(true);
    try {
      await sendSupplierAssignmentEmail(issue, supplier, '');
      toast.success(`E-post skickad till ${supplier.name || supplier.email}`);
    } catch (error) {
      console.error('Failed to send supplier email:', error);
      toast.error('Kunde inte skicka e-post');
    } finally {
      setSendingSupplierEmail(false);
    }
  };

  // Send SMS to supplier
  const handleSendSupplierSMS = async () => {
    if (!issue?.supplier_id) {
      toast.error('Ingen leverantör tilldelad');
      return;
    }
    
    const supplier = suppliers.find(s => s.id === issue.supplier_id);
    if (!supplier?.phone) {
      toast.error('Leverantören saknar telefonnummer');
      return;
    }
    
    setSendingSupplierSMS(true);
    try {
      // Create work order URL
      const workOrderUrl = `${window.location.origin}/orders?issue=${issue.id}`;
      await sendWorkOrderSMS(supplier, issue, workOrderUrl);
      toast.success(`SMS skickat till ${supplier.name || supplier.phone}`);
    } catch (error) {
      console.error('Failed to send supplier SMS:', error);
      toast.error('Kunde inte skicka SMS');
    } finally {
      setSendingSupplierSMS(false);
    }
  };

  const handleAssignmentChange = async (assignedType: 'internal' | 'supplier', supplierId: string | null = null) => {
    setUpdating(true);
    try {
      if (!id) {
        toast.error('Ärende-ID saknas');
        return;
      }
      const updateData = { 
        assigned_type: assignedType,
        supplier_id: assignedType === 'supplier' ? (supplierId || undefined) : undefined 
      };
      const { data } = await updateIssue(id, updateData);
      setIssue(data);
      toast.success('Tilldelning uppdaterad');
    } catch (error) {
      toast.error(t('error_occurred'));
    } finally {
      setUpdating(false);
    }
  };

  const handleCreateOrder = async () => {
    // For external assignments, require supplier info
    const isInternal = issue?.assigned_type === 'internal';
    
    if (!isInternal && (!orderForm.subcontractor_name || !orderForm.subcontractor_email)) {
      toast.error('Fyll i leverantörens namn och e-post');
      return;
    }
    if (!orderForm.instructions) {
      toast.error('Instruktioner krävs');
      return;
    }
    
    try {
      const orderData: {
        issue_id: string;
        instructions: string;
        instructions_html: string;
        due_date: string;
        is_internal: boolean;
        subcontractor_name?: string;
        subcontractor_email?: string;
        subcontractor_phone?: string;
      } = { 
        issue_id: id || '', 
        instructions: orderForm.instructions,
        instructions_html: orderForm.instructions_html,
        due_date: orderForm.due_date,
        is_internal: isInternal
      };
      
      // Only add supplier info for external assignments
      if (!isInternal) {
        orderData.subcontractor_name = orderForm.subcontractor_name;
        orderData.subcontractor_email = orderForm.subcontractor_email;
        orderData.subcontractor_phone = orderForm.subcontractor_phone;
      } else {
        // For internal, use organization name
        orderData.subcontractor_name = 'Intern';
        orderData.subcontractor_email = 'intern@foreningen.se';
      }
      
      const response = await createOrder(orderData);
      toast.success(isInternal ? 'Intern arbetsorder skapad' : t('order_sent'));
      setOrderDialogOpen(false);
      setExistingOrder(response.data);
      setOrderViewOpen(true);
      setOrderForm({
        subcontractor_name: '',
        subcontractor_email: '',
        subcontractor_phone: '',
        instructions: '',
        instructions_html: '',
        due_date: '',
        include_history: true,
        include_internal_notes: false
      });
      setShowPreview(false);
    } catch (error) {
      toast.error(t('error_occurred'));
    }
  };

  // Delete issue with double confirmation
  const handleDeleteIssue = () => {
    toast(
      <div className="flex flex-col gap-2">
        <span className="font-medium">Vill du ta bort detta ärende?</span>
        <span className="text-sm text-slate-500">"{issue?.title}"</span>
        <div className="flex gap-2 mt-1">
          <Button 
            size="sm" 
            variant="destructive" 
            className="h-7 text-xs"
            onClick={() => {
              toast.dismiss();
              confirmDeleteIssue();
            }}
          >
            Ja, ta bort
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            className="h-7 text-xs"
            onClick={() => toast.dismiss()}
          >
            Avbryt
          </Button>
        </div>
      </div>,
      { duration: 10000 }
    );
  };

  const confirmDeleteIssue = () => {
    toast(
      <div className="flex flex-col gap-2">
        <span className="font-medium text-red-600">Bekräfta borttagning</span>
        <span className="text-sm text-slate-500">Denna åtgärd kan inte ångras.</span>
        <div className="flex gap-2 mt-1">
          <Button 
            size="sm" 
            variant="destructive" 
            className="h-7 text-xs"
            onClick={async () => {
              if (!id) return;
              toast.dismiss();
              try {
                await deleteIssue(id);
                toast.success('Ärendet har tagits bort');
                navigate('/issues');
              } catch (error) {
                toast.error('Kunde inte ta bort ärendet');
              }
            }}
          >
            Bekräfta
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            className="h-7 text-xs"
            onClick={() => toast.dismiss()}
          >
            Avbryt
          </Button>
        </div>
      </div>,
      { duration: 10000 }
    );
  };

  const getStatusConfig = (status: string | undefined) => {
    const configs: Record<string, { label: string; bg: string }> = {
      open: { label: t('open'), bg: 'bg-slate-100 text-slate-700' },
      in_progress: { label: t('in_progress'), bg: 'bg-amber-100 text-amber-800' },
      waiting: { label: t('waiting'), bg: 'bg-orange-100 text-orange-700' },
      resolved: { label: t('resolved'), bg: 'bg-emerald-100 text-emerald-700' },
      closed: { label: t('closed'), bg: 'bg-slate-200 text-slate-600' }
    };
    return configs[status || 'open'] || configs.open;
  };

  const getUrgencyConfig = (level: number | undefined) => {
    const configs: Record<number, { label: string; color: string; textColor: string; bgLight: string }> = {
      1: { label: t('urgency_1'), color: 'bg-emerald-500', textColor: 'text-emerald-700', bgLight: 'bg-emerald-50' },
      2: { label: t('urgency_2'), color: 'bg-lime-500', textColor: 'text-lime-700', bgLight: 'bg-lime-50' },
      3: { label: t('urgency_3'), color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50' },
      4: { label: t('urgency_4'), color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-50' },
      5: { label: t('urgency_5'), color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50' }
    };
    return configs[level || 2] || { label: String(level), color: 'bg-slate-400', textColor: 'text-slate-600', bgLight: 'bg-slate-50' };
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-5 w-24 bg-slate-200 rounded" />
          <div className="h-7 w-48 bg-slate-200 rounded" />
          <div className="flex gap-2">
            <div className="h-6 w-20 bg-slate-200 rounded-full" />
            <div className="h-6 w-24 bg-slate-200 rounded-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!issue) return null;

  const statusConfig = getStatusConfig(issue.status);
  const urgencyConfig = getUrgencyConfig(issue.urgency_level);
  const assignedSupplier = suppliers.find(s => s.id === issue.supplier_id);
  
  // Get active suppliers for the issue category
  const availableSuppliers = suppliers.filter(s => 
    s.is_active && (s.category === issue.category || s.category === 'other')
  );

  return (
    <div className="p-4 md:p-6 max-w-5xl" data-testid="issue-detail-page">
      {/* Compact Header - Mobile Responsive */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <button
          onClick={() => navigate('/issues')}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors flex-shrink-0"
          data-testid="back-to-issues"
        >
          <ArrowLeft size={14} weight="bold" />
          <span className="hidden sm:inline">Tillbaka</span>
        </button>
        
        {isAdmin && (
          <div className="flex gap-1 sm:gap-1.5 flex-wrap justify-end">
            <Link to={`/issues/${id}/conversation`}>
              <Button variant="ghost" size="sm" className="h-7 px-1.5 sm:px-2 text-xs" data-testid="email-thread-button">
                <EnvelopeSimple size={14} className="sm:mr-1" />
                <span className="hidden sm:inline">Konversation</span>
              </Button>
            </Link>
            
            {existingOrder ? (
              /* Show existing order modal */
              <OrderViewDialog
                open={orderViewOpen}
                onOpenChange={setOrderViewOpen}
                order={existingOrder}
                conversationHtml={buildConversationHTML(orderForm.include_internal_notes || false)}
              />
            ) : (
              /* Create new order dialog */
              <CreateOrderDialog
                open={orderDialogOpen}
                onOpenChange={handleOrderDialogOpen}
                isInternalAssignment={issue.assigned_type === 'internal'}
                suppliers={suppliers}
                selectedSupplierId={selectedSupplierForOrder}
                onSupplierSelect={handleSupplierSelect}
                form={orderForm}
                onFormChange={(newForm) => setOrderForm(newForm)}
                showPreview={showPreview}
                onPreviewToggle={() => setShowPreview(!showPreview)}
                onSubmit={handleCreateOrder}
              />
            )}
            
            
            {/* Delete Issue Button */}
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 px-1.5 sm:px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleDeleteIssue}
              data-testid="delete-issue-button"
            >
              <Trash size={14} />
            </Button>
          </div>
        )}
      </div>

      {/* Title + Status Row - Mobile Responsive */}
      <div className="flex items-start gap-2 sm:gap-3 mb-3">
        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${urgencyConfig.color}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 flex-wrap">
            {issue.issue_number && (
              <span className="text-[10px] sm:text-xs font-mono text-slate-500">{issue.issue_number}</span>
            )}
            {issue.source === 'email' && (
              <span className="text-[9px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                via e-post
              </span>
            )}
          </div>
          <h1 className="text-base sm:text-lg font-semibold text-slate-900 leading-tight line-clamp-2 sm:truncate">
            {issue.title}
          </h1>
        </div>
        <span className={`px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium flex-shrink-0 ${statusConfig.bg}`}>
          {statusConfig.label}
        </span>
      </div>

      {/* Compact Metadata Bar - Mobile Responsive */}
      <div className="flex flex-wrap items-center gap-x-2 sm:gap-x-4 gap-y-1.5 text-[10px] sm:text-xs text-slate-500 mb-4 pb-4 border-b border-slate-100">
        <span className="flex items-center gap-1">
          <Tag size={12} />
          {t(issue.category || 'other')}
        </span>
        <span className={`flex items-center gap-1 px-1 sm:px-1.5 py-0.5 rounded ${urgencyConfig.bgLight} ${urgencyConfig.textColor}`}>
          <Warning size={12} />
          {urgencyConfig.label}
        </span>
        <span className="flex items-center gap-1">
          {issue.assigned_type === 'supplier' ? (
            <>
              <Truck size={12} className="text-blue-600" />
              <span className="text-blue-700 font-medium truncate max-w-[80px] sm:max-w-none">{assignedSupplier?.name || 'Leverantör'}</span>
            </>
          ) : (
            <>
              <House size={12} />
              Internt
            </>
          )}
        </span>
        {issue.reporter_name && (
          <span className="flex items-center gap-1 w-full sm:w-auto order-last sm:order-none">
            <User size={12} />
            <span className="truncate">{issue.reporter_name}</span>
            {issue.reporter_email && <span className="text-slate-400 truncate hidden sm:inline">({issue.reporter_email})</span>}
          </span>
        )}
        <span className="flex items-center gap-1 ml-auto">
          <Calendar size={12} />
          {issue.created_at ? new Date(issue.created_at).toLocaleDateString('sv-SE') : '-'}
        </span>
      </div>

      {/* Main Content */}
      <div className="space-y-4">
        {/* Conversation Flow - Mobile Responsive */}
        <div className="bg-white rounded-lg border border-slate-200 p-3 sm:p-4">
          <div className="flex items-center gap-2 text-[10px] sm:text-xs font-medium text-slate-700 mb-3 sm:mb-4">
            <ChatText size={14} />
            Ärendekonversation
          </div>
          <IssueConversation
            issue={issue}
            emailThread={emailThread ?? undefined}
            order={existingOrder ?? undefined}
            onNoteAdded={(note) => {
              setIssue(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  internal_notes: [...(prev.internal_notes || []), note]
                };
              });
            }}
            showNoteInput={isAdmin}
          />
        </div>

        {/* Attachments */}
        {issue.attachments && issue.attachments.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {issue.attachments.map((url) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                {url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                  <img
                    src={url}
                    alt=""
                    className="w-16 h-16 object-cover rounded border border-slate-200 group-hover:opacity-80 transition-opacity"
                  />
                ) : (
                  <div className="w-16 h-16 flex items-center justify-center bg-slate-50 rounded border border-slate-200 group-hover:bg-slate-100">
                    <File size={18} className="text-slate-400" />
                  </div>
                )}
              </a>
            ))}
          </div>
        )}

        {/* Resolution */}
        {issue.resolution && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <CheckCircle size={14} className="text-emerald-600" weight="fill" />
              <span className="text-xs font-semibold text-emerald-800">Lösning</span>
            </div>
            <p className="text-sm text-emerald-700 whitespace-pre-wrap">
              {issue.resolution}
            </p>
          </div>
        )}

        {/* Admin Controls - Mobile Responsive */}
        {isAdmin && (
          <div className="bg-slate-50 rounded-lg p-3 space-y-3">
            <div className="flex items-center gap-2 text-[10px] sm:text-xs font-medium text-slate-700 mb-2">
              <Wrench size={12} />
              Hantera ärende
            </div>
            
            <div className="grid grid-cols-1 gap-3">
              {/* Status */}
              <div className="space-y-1">
                <label className="text-[10px] sm:text-xs text-slate-500">Status</label>
                <Select
                  value={issue.status}
                  onValueChange={handleStatusChange}
                  disabled={updating}
                >
                  <SelectTrigger className="h-8 text-xs" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open" className="text-xs">{t('open')}</SelectItem>
                    <SelectItem value="in_progress" className="text-xs">{t('in_progress')}</SelectItem>
                    <SelectItem value="waiting" className="text-xs">{t('waiting')}</SelectItem>
                    <SelectItem value="resolved" className="text-xs">{t('resolved')}</SelectItem>
                    <SelectItem value="closed" className="text-xs">{t('closed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Assignment Type */}
              <div className="space-y-1">
                <label className="text-[10px] sm:text-xs text-slate-500">Tilldelning</label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => handleAssignmentChange('internal')}
                    disabled={updating}
                    className={`flex-1 flex items-center justify-center gap-1 h-8 rounded text-xs font-medium transition-colors ${
                      issue.assigned_type === 'internal'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-slate-200 hover:bg-slate-50 text-slate-600'
                    }`}
                    data-testid="change-assign-internal"
                  >
                    <House size={12} />
                    Internt
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (issue.assigned_type !== 'supplier') {
                        handleAssignmentChange('supplier', null);
                      }
                    }}
                    disabled={updating}
                    className={`flex-1 flex items-center justify-center gap-1 h-8 rounded text-xs font-medium transition-colors ${
                      issue.assigned_type === 'supplier'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-slate-200 hover:bg-slate-50 text-slate-600'
                    }`}
                    data-testid="change-assign-supplier"
                  >
                    <Truck size={12} />
                    Leverantör
                  </button>
                </div>
              </div>
            </div>

            {/* Supplier Select */}
            {issue.assigned_type === 'supplier' && (
              <div className="space-y-1">
                <label className="text-xs text-slate-500">Välj leverantör</label>
                <Select
                  value={issue.supplier_id || ''}
                  onValueChange={(value) => handleAssignmentChange('supplier', value)}
                  disabled={updating}
                >
                  <SelectTrigger className="h-8 text-xs" data-testid="supplier-assignment-select">
                    <SelectValue placeholder="Välj leverantör..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSuppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id} className="text-xs">
                        {supplier.name}
                      </SelectItem>
                    ))}
                    {availableSuppliers.length === 0 && (
                      <SelectItem value="no-suppliers" disabled className="text-xs">Inga aktiva leverantörer</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                
                {/* Send Email to Supplier Button */}
                {issue.supplier_id && (
                  <div className="flex gap-2 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSendSupplierEmail}
                      disabled={sendingSupplierEmail}
                      className="flex-1 text-xs h-8"
                      data-testid="send-supplier-email-btn"
                    >
                      <EnvelopeSimple size={14} className="mr-1.5" />
                      {sendingSupplierEmail ? 'Skickar...' : 'E-post'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSendSupplierSMS}
                      disabled={sendingSupplierSMS}
                      className="flex-1 text-xs h-8"
                      data-testid="send-supplier-sms-btn"
                    >
                      <DeviceMobile size={14} className="mr-1.5" />
                      {sendingSupplierSMS ? 'Skickar...' : 'SMS'}
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {/* Send Resolved Email to Reporter */}
            {(issue.status === 'resolved' || issue.status === 'closed') && issue.reporter_email && (
              <div className="pt-3 border-t border-slate-100">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendResolvedEmail}
                  disabled={sendingResolvedEmail}
                  className="w-full text-xs h-8 border-green-200 text-green-700 hover:bg-green-50"
                  data-testid="send-resolved-email-btn"
                >
                  <EnvelopeSimple size={14} className="mr-1.5" />
                  {sendingResolvedEmail ? 'Skickar...' : 'Skicka bekräftelse till rapportör'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Timestamps - Mobile Responsive */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 text-[9px] sm:text-[10px] text-slate-400 pt-2 border-t border-slate-100">
          <span>Skapad: {issue.created_at ? new Date(issue.created_at).toLocaleString('sv-SE') : '-'}</span>
          <span>Uppdaterad: {issue.updated_at ? new Date(issue.updated_at).toLocaleString('sv-SE') : '-'}</span>
        </div>
      </div>
    </div>
  );
}
